import { FC, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { BottomSheet } from '@beeline/design-system-react/extracted-styles';
import { BottomSheetActionsType } from '@beeline/design-system-react/types/components/BottomSheet/BottomSheet.types';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import { useHost } from 'src/providers/HostProvider';
import { getSearchState } from 'src/store/search/selectors';
import { searchActions } from 'src/store/search/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';

import { SearchFiltersFields } from '../SearchFiltersFields';
import { SearchFiltersForm } from '../SearchFiltersForm';

import classes from './FiltersBottomSheet.module.scss';

const Fields: FC = () => {
  const dispatch = useAppDispatch();

  const { submit, reset } = useForm();

  const { filtersAreOpened } = useSelector(getSearchState);

  const close = useCallback(() => {
    dispatch(searchActions.closeFilters());
  }, [dispatch]);

  const applyFilters = useCallback(async () => {
    await submit();
    close();
    reset();
  }, [close, reset, submit]);

  const resetFilters = useCallback(async () => {
    dispatch(searchActions.resetFilters());

    close();
    reset();
  }, [close, dispatch, reset]);

  const actions = useMemo((): BottomSheetActionsType | undefined => {
    const result: BottomSheetActionsType = [
      {
        label: 'Сбросить',
        variant: 'outlined',
        onClick: resetFilters,
      },
      {
        label: 'Применить',
        variant: 'contained',
        onClick: applyFilters,
      },
    ];

    return result;
  }, [applyFilters, resetFilters]);

  const handleSetOpen = useCallback(
    (value: boolean) => {
      dispatch(searchActions.setFiltersAreOpened(value));
      reset();
    },
    [dispatch, reset],
  );

  return (
    <BottomSheet
      isOpen={filtersAreOpened}
      handleSetOpen={handleSetOpen}
      swiper
      modal={false}
      snapPoints={[1]}
      className={classes.settings}
      initialSnapPoint={0}
      header={{
        icon: Icons.Filter,
        title: 'Фильтры',
        closeButton: true,
      }}
      actions={actions}
    >
      <div className={classes.container}>
        <SearchFiltersFields />
      </div>
    </BottomSheet>
  );
};

const FormBottomSheet: FC = () => {
  return (
    <SearchFiltersForm>
      <Fields />
    </SearchFiltersForm>
  );
};

export const FiltersBottomSheet: FC = () => {
  const { host } = useHost();

  if (!host) return null;

  return createPortal(<FormBottomSheet />, host);
};
